import React, { Component, ErrorInfo, ReactNode } from 'react';
import './common.css';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onRetry?: () => void;
  maxRetries?: number;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  retryCount: number;
  isRetrying: boolean;
  lastErrorTime: number;
}

class APIErrorBoundary extends Component<Props, State> {
  private retryTimeout: NodeJS.Timeout | null = null;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
      isRetrying: false,
      lastErrorTime: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
      lastErrorTime: Date.now(),
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('APIErrorBoundary caught error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo,
    });

    // Check if this is a transient error that might benefit from automatic retry
    if (this.isTransientError(error) && this.shouldAutoRetry()) {
      this.scheduleAutoRetry();
    }
  }

  componentWillUnmount() {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }
  }

  private isTransientError(error: Error): boolean {
    const errorMessage = error.message.toLowerCase();
    const errorStack = error.stack?.toLowerCase() || '';
    
    // Check for transient error patterns
    const transientPatterns = [
      'network error',
      'fetch error',
      'connection failed',
      'timeout',
      'temporary',
      'service unavailable',
      'server error',
      'internal server error',
      'bad gateway',
      'gateway timeout',
      'connection reset',
      'connection refused',
    ];

    return transientPatterns.some(pattern => 
      errorMessage.includes(pattern) || errorStack.includes(pattern)
    );
  }

  private shouldAutoRetry(): boolean {
    const maxRetries = this.props.maxRetries || 2;
    const timeSinceLastError = Date.now() - this.state.lastErrorTime;
    const minRetryInterval = 1000; // 1 second minimum between retries
    
    return (
      this.state.retryCount < maxRetries &&
      timeSinceLastError >= minRetryInterval &&
      !this.state.isRetrying
    );
  }

  private scheduleAutoRetry = () => {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }

    // Exponential backoff: 1s, 2s, 4s, etc.
    const retryDelay = Math.min(1000 * Math.pow(2, this.state.retryCount), 5000);
    
    this.setState({ isRetrying: true });
    
    this.retryTimeout = setTimeout(() => {
      this.handleRetry(true);
    }, retryDelay);
  };

  private handleRetry = (isAutoRetry: boolean = false) => {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
    }

    // Call the onRetry callback if provided
    if (this.props.onRetry) {
      try {
        this.props.onRetry();
      } catch (retryError) {
        console.error('Error in retry callback:', retryError);
      }
    }

    // Reset error state and increment retry count
    this.setState(prevState => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prevState.retryCount + 1,
      isRetrying: false,
      lastErrorTime: 0,
    }));
  };

  private handleReset = () => {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
    }

    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
      isRetrying: false,
      lastErrorTime: 0,
    });
  };

  private getErrorMessage(): string {
    if (!this.state.error) return 'Errore sconosciuto';

    const error = this.state.error;
    
    // Check for specific error types and provide user-friendly messages
    if (error.message.toLowerCase().includes('network')) {
      return 'Problema di connessione di rete. Controlla la tua connessione internet.';
    }
    
    if (error.message.toLowerCase().includes('timeout')) {
      return 'La richiesta ha impiegato troppo tempo. Riprova tra qualche momento.';
    }
    
    if (error.message.toLowerCase().includes('server error') || 
        error.message.toLowerCase().includes('internal server error')) {
      return 'Errore del server. Il nostro team è stato notificato.';
    }
    
    if (error.message.toLowerCase().includes('service unavailable')) {
      return 'Il servizio è temporaneamente non disponibile. Riprova tra qualche minuto.';
    }
    
    // For API errors, try to extract meaningful message
    if (error.message.includes('Request failed')) {
      return 'Errore nella comunicazione con il server. Riprova.';
    }
    
    // Default fallback
    return 'Si è verificato un errore imprevisto. Riprova o ricarica la pagina.';
  }

  private getErrorTitle(): string {
    if (!this.state.error) return 'Errore';

    const error = this.state.error;
    
    if (error.message.toLowerCase().includes('network')) {
      return 'Problema di connessione';
    }
    
    if (error.message.toLowerCase().includes('timeout')) {
      return 'Timeout della richiesta';
    }
    
    if (error.message.toLowerCase().includes('server error')) {
      return 'Errore del server';
    }
    
    return 'Errore dell\'applicazione';
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const maxRetries = this.props.maxRetries || 2;
      const canRetry = this.state.retryCount < maxRetries;
      const isTransient = this.state.error ? this.isTransientError(this.state.error) : false;

      return (
        <div className="error-boundary">
          <div className="error-boundary-content">
            <h2>{this.getErrorTitle()}</h2>
            <p>{this.getErrorMessage()}</p>
            
            {this.state.isRetrying && (
              <div className="loading-spinner loading-spinner-medium">
                <div className="spinner"></div>
                <p className="loading-message">Nuovo tentativo in corso...</p>
              </div>
            )}
            
            {!this.state.isRetrying && (
              <div className="error-actions">
                {canRetry && (
                  <button 
                    onClick={() => this.handleRetry(false)}
                    className="btn btn-primary"
                  >
                    Riprova {this.state.retryCount > 0 && `(${this.state.retryCount}/${maxRetries})`}
                  </button>
                )}
                
                <button 
                  onClick={this.handleReset}
                  className="btn btn-secondary"
                >
                  Reset
                </button>
                
                <button 
                  onClick={() => window.location.reload()}
                  className="btn btn-outline"
                >
                  Ricarica pagina
                </button>
              </div>
            )}

            {this.state.retryCount >= maxRetries && isTransient && (
              <div className="error-display error-display-warning" style={{ marginTop: '1rem' }}>
                <p>
                  <strong>Suggerimento:</strong> Se il problema persiste, prova a ricaricare la pagina 
                  o controlla la tua connessione internet.
                </p>
              </div>
            )}

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="error-details">
                <summary>Dettagli errore (solo sviluppo)</summary>
                <div>
                  <p><strong>Retry Count:</strong> {this.state.retryCount}</p>
                  <p><strong>Is Transient:</strong> {isTransient ? 'Yes' : 'No'}</p>
                  <p><strong>Can Auto-Retry:</strong> {this.shouldAutoRetry() ? 'Yes' : 'No'}</p>
                </div>
                <pre className="error-stack">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default APIErrorBoundary;